import { z } from "zod";

import { countries, validCountryCodes } from "~/shared/constants/country.constants";
import { type ShipmentArrangementContext, ShipmentArrangementSchema } from "~/shared/orders/shipment-arrangement";
import { normalizeCountry } from "~/shared/utils/address-normalization";

// No defaults here: omitted updates preserve saved values, including an explicit false or empty note.
export const ShipmentMetadataSchema = z.object({
  shipmentArrangement: ShipmentArrangementSchema.optional(),
  freightMethod: z.string().nullish(),
  hasBorderCrossing: z.boolean().optional(),
  borderCrossingPointId: z.string().uuid().nullish(),
  borderCrossingPoint: z.string().nullish(),
  portOfEntryId: z.string().uuid().nullish(),
  portOfEntry: z.string().nullish(),
  borderCrossingDate: z.date().nullish(),
  borderCrossingTime: z
    .string()
    .regex(/^(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d$/, "Time must use HH:mm:ss.")
    .nullish(),
  borderCrossingNotes: z.string().nullish(),
});
export type ShipmentMetadata = z.infer<typeof ShipmentMetadataSchema>;

export const SalesOrderShipmentMetadataSchema = ShipmentMetadataSchema.extend({
  shipOnBehalf: z.boolean().optional(),
  includeDrumHazardPage: z.boolean().optional(),
});
export type SalesOrderShipmentMetadata = z.infer<typeof SalesOrderShipmentMetadataSchema>;

const OrderHeaderShipmentMetadataSchema = SalesOrderShipmentMetadataSchema.omit({ freightMethod: true });

/** Freight Method already belongs to the historical SO header contract. */
export const hasOrderShipmentMetadataInput = (input: SalesOrderShipmentMetadata): boolean =>
  Object.values(OrderHeaderShipmentMetadataSchema.parse(input)).some(value => value !== undefined);

export const hasShipmentMetadataInput = (input: ShipmentMetadata): boolean =>
  Object.values(ShipmentMetadataSchema.parse(input)).some(value => value !== undefined);

/** Freight Method belongs to each order; only arrangement and border details are shared. */
export const hasSharedShipmentMetadataInput = (input: ShipmentMetadata): boolean =>
  Object.values(ShipmentMetadataSchema.omit({ freightMethod: true }).parse(input)).some(value => value !== undefined);

export const PurchaseOrderItemSourceMetadataSchema = z.object({
  producerSupplierId: z.string().uuid().nullish(),
  supplierReleaseNumber: z.string().nullish(),
});
export type PurchaseOrderItemSourceMetadata = z.infer<typeof PurchaseOrderItemSourceMetadataSchema>;

/** Pickup ownership is separate from the commercial supplier used for purchasing and certificates. */
export function isPurchaseOrderPickupOwner(ownerSupplierId: string, supplierId: string, producerSupplierIds: readonly string[]): boolean {
  return ownerSupplierId === supplierId || producerSupplierIds.includes(ownerSupplierId);
}

type CountryInput = string | null | undefined;
const countryCodeByName = new Map(countries.map(country => [country.name.toUpperCase(), country.code]));

/** Unknown country text is not evidence that a shipment crosses a border. */
export function normalizeShipmentCountry(value: CountryInput): string | null {
  const normalized = normalizeCountry(value)?.toUpperCase();
  if (!normalized) {
    return null;
  }
  return validCountryCodes.has(normalized) ? normalized : (countryCodeByName.get(normalized) ?? null);
}

/** Undefined lets untouched create forms wait until both current endpoints have loaded. */
export function inferHasBorderCrossing(originCountry: CountryInput, destinationCountry: CountryInput): boolean | undefined {
  const origin = normalizeShipmentCountry(originCountry);
  const destination = normalizeShipmentCountry(destinationCountry);
  return origin && destination ? origin !== destination : undefined;
}

/** Creation boundary only; a supplied value, including false, wins over geography. */
export function resolveHasBorderCrossing(
  explicitValue: boolean | undefined,
  originCountry: CountryInput,
  destinationCountry: CountryInput,
): boolean {
  return explicitValue ?? inferHasBorderCrossing(originCountry, destinationCountry) ?? false;
}

export function isCanadianShipment(originCountry: CountryInput, destinationCountry: CountryInput): boolean {
  return normalizeShipmentCountry(originCountry) === "CA" || normalizeShipmentCountry(destinationCountry) === "CA";
}

export function getShipmentRouteCountries(
  context: ShipmentArrangementContext,
  countries: { warehouseCountry?: CountryInput; customerCountry?: CountryInput; supplierCountry?: CountryInput },
): { originCountry: string | null; destinationCountry: string | null } {
  return {
    originCountry: normalizeShipmentCountry(context === "salesOrder" ? countries.warehouseCountry : countries.supplierCountry),
    destinationCountry: normalizeShipmentCountry(context === "purchaseOrder" ? countries.warehouseCountry : countries.customerCountry),
  };
}
